import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Shield, Video, Mic, Users, Calendar, Megaphone, Smartphone, Star, Camera, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { useState } from "react";

// Translations moved to translations.ts
const mediaIcons = [Camera, Video, Mic, Users, Calendar, Smartphone, Megaphone, Star, CheckCircle2];

const Media = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [displayTab, setDisplayTab] = useState(0);
  const [animating, setAnimating] = useState(false);

  const rawMediaData = t("media.items");
  const mediaData = Array.isArray(rawMediaData) ? rawMediaData : [];
  const active = mediaData[displayTab] || (mediaData.length > 0 ? mediaData[0] : {});
  const ActiveIcon = mediaIcons[displayTab % mediaIcons.length] || Camera;

  const handleTabChange = (i: number) => {
    if (i === activeTab || animating) return;
    setAnimating(true);
    setActiveTab(i);
    setTimeout(() => {
      setDisplayTab(i);
      setAnimating(false);
    }, 280);
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={t('media.title1') + ' ' + t('media.title2')}
        description={t('media.badge')}
        canonical="/media"
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0 bg-navy-950">
            <img 
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp" 
              alt="Media Services" 
              className="w-full h-full object-cover object-center opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {t('media.badge')}
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                {t('media.title1')} <span className="text-primary">{t('media.title2')}</span>
              </h1>
              <Link to="/free-consultation">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg">
                  {t('media.btn')} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Services Tabs Section */}
        <section className="py-24 bg-slate-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">{t('media.expertise')}</div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-navy-950">
                {t('media.compTitle1')} <span className="text-primary">{t('media.compTitle2')}</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 max-w-6xl mx-auto">
              {/* Left: vertical nav pills */}
              <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 scrollbar-none">
                {mediaData.map((service, i) => {
                  const NavIcon = mediaIcons[i % mediaIcons.length];
                  const isActive = activeTab === i;
                  return (
                    <button
                      key={service.id}
                      onClick={() => handleTabChange(i)}
                      className={`group flex items-center gap-3 px-5 py-4 rounded-2xl text-left transition-all duration-300 shrink-0 lg:shrink border ${
                        isActive
                          ? "bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20 scale-[1.02]"
                          : "bg-white text-slate-600 border-slate-200 hover:border-primary/30 hover:bg-primary/5 hover:text-navy-950"
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isActive ? "bg-white/20" : "bg-slate-100 group-hover:bg-primary/10"
                      }`}>
                        <NavIcon className={`w-5 h-5 transition-colors duration-300 ${isActive ? "text-primary-foreground" : "text-primary"}`} strokeWidth={1.8} />
                      </div>
                      <span className="font-semibold text-sm whitespace-nowrap lg:whitespace-normal leading-tight">{service.title}</span>
                      {isActive && <ArrowRight className="ml-auto w-4 h-4 opacity-70 hidden lg:block" />}
                    </button>
                  );
                })}
              </div>

              {/* Right: animated content panel */}
              <div
                className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden"
                style={{
                  opacity: animating ? 0 : 1,
                  transform: animating ? "translateY(16px) scale(0.98)" : "translateY(0) scale(1)",
                  transition: "opacity 0.28s cubic-bezier(0.4,0,0.2,1), transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                  {/* Content */}
                  <div className="p-8 md:p-12 flex flex-col justify-between">
                    <div>
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-8">
                        <ActiveIcon className="w-7 h-7 text-primary" strokeWidth={1.8} />
                      </div>
                      <div className="text-primary font-bold tracking-widest uppercase text-xs mb-3">{active.title}</div>
                      <h3 className="text-3xl md:text-4xl font-black text-navy-950 leading-tight mb-4">{active.subheading}</h3>
                      <p className="text-slate-500 leading-relaxed text-base mb-8">{active.desc}</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Link to="/free-consultation">
                        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl px-6 h-12 font-semibold">
                          {active.btn1} <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/free-consultation">
                        <Button variant="outline" className="border-slate-200 text-navy-950 hover:bg-slate-50 hover:text-primary rounded-xl px-6 h-12 font-semibold bg-white">
                          {active.btn2}
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Visual panel */}
                  <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 flex items-center justify-center p-10 relative overflow-hidden min-h-[260px]">
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-64 h-64 rounded-full border border-primary/10 absolute animate-ping" style={{ animationDuration: "4s" }} />
                      <div className="w-48 h-48 rounded-full border border-primary/15 absolute" />
                      <div className="w-32 h-32 rounded-full border border-primary/20 absolute" />
                    </div>
                    <div className="relative z-10 w-28 h-28 rounded-3xl bg-white shadow-2xl shadow-primary/20 flex items-center justify-center border border-primary/10">
                      <ActiveIcon className="w-14 h-14 text-primary" strokeWidth={1.2} />
                    </div>
                    <div className="absolute top-4 right-6 text-[80px] font-black text-primary/5 leading-none select-none pointer-events-none">
                      {String(displayTab + 1).padStart(2, "0")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Happy Customers Section */}
        <section className="py-24 bg-navy-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp')] opacity-10 bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/90 to-navy-950"></div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-16">{t('media.customers')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {(t('media.features') as any[]).map((item, i) => {
                const Icon = i === 0 ? ShieldCheck : i === 1 ? CheckCircle2 : Shield;
                return (
                  <div key={i} className="bg-white/5 border border-white/10 p-10 rounded-3xl backdrop-blur-sm hover:-translate-y-2 hover:bg-white/10 transition-all duration-300 group shadow-2xl">
                    <div className="w-16 h-16 rounded-2xl bg-primary/20 text-primary flex items-center justify-center mb-8 mx-auto group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                    <p className="text-white/70 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>



        {/* Stats & Hand Image Section */}
        <section className="py-24 bg-white overflow-hidden border-t border-slate-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-center gap-16 max-w-7xl mx-auto">
              <div className="lg:w-1/2 relative group">
                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                <img 
                  src="https://static.wixstatic.com/media/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png/v1/fill/w_960,h_706,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_692872a1103a43c3b151fb190660505e~mv2.png" 
                  alt="AITIM Capital Card" 
                  className="w-full max-w-lg mx-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)] group-hover:-translate-y-4 group-hover:scale-105 transition-all duration-700 relative z-10"
                />
              </div>
              <div className="lg:w-1/2">
                <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-xs mb-6 tracking-widest uppercase">
                  {t('media.whyBadge')}
                </div>
                <h2 className="text-5xl md:text-7xl font-black text-navy-950 mb-16 tracking-tight">AITIM GROUP</h2>
                
                <div className="grid grid-cols-2 gap-x-8 gap-y-12">
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-primary mb-4 group-hover:scale-110 origin-left transition-transform duration-300">10+</div>
                    <div className="h-0.5 bg-slate-200 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-sm font-bold tracking-widest text-slate-500 uppercase group-hover:text-navy-950 transition-colors" dangerouslySetInnerHTML={{__html: t('media.exp').replace(' ', '<br/>')}}></div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-primary mb-4 group-hover:scale-110 origin-left transition-transform duration-300">7000+</div>
                    <div className="h-0.5 bg-slate-200 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-sm font-bold tracking-widest text-slate-500 uppercase group-hover:text-navy-950 transition-colors" dangerouslySetInnerHTML={{__html: t('media.trucks').replace(' ', '<br/>')}}></div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-primary mb-4 group-hover:scale-110 origin-left transition-transform duration-300">100%</div>
                    <div className="h-0.5 bg-slate-200 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-sm font-bold tracking-widest text-slate-500 uppercase group-hover:text-navy-950 transition-colors" dangerouslySetInnerHTML={{__html: t('media.sat').replace(' ', '<br/>')}}></div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-6xl font-light text-primary mb-4 group-hover:scale-110 origin-left transition-transform duration-300">20+</div>
                    <div className="h-0.5 bg-slate-200 w-full mb-4 relative overflow-hidden">
                      <div className="absolute top-0 left-0 h-full bg-primary w-0 group-hover:w-full transition-all duration-500"></div>
                    </div>
                    <div className="text-sm font-bold tracking-widest text-slate-500 uppercase group-hover:text-navy-950 transition-colors" dangerouslySetInnerHTML={{__html: t('media.awards').replace(' ', '<br/>')}}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Media;
